import {
  findIndex,
  has,
  head,
  isNil,
  keys,
  lensPath,
  merge,
  mergeDeepRight,
  propEq,
  set,
  update,
} from "ramda";
import {
  type AllUsersActionTypes,
  AllUsersRequestKey,
  TypeKeys,
} from "actions/users/all_users";
import {
  AnalyticsRequestKey,
  TypeKeys as AnalyticsTypeKeys,
  type AnalyticsUserActionTypes,
} from "actions/users/analytics";
import {
  BreadcrumbRequestKey,
  TypeKeys as BreadcrumbTypeKeys,
  type BreadcrumbUserActionTypes,
} from "actions/users/breadcrumb";
import {
  InventoryRequestKey,
  TypeKeys as InventoryTypeKeys,
  type InventoryUserActionTypes,
} from "actions/users/inventory";
import { replaceInCollection } from "helpers/collections";
import { findUserByEmail } from "reducers/users_selectors";
import { RequestStatus } from "types/request";
import type { IUsers } from "types/user";
import type { IAppUser } from "types/user/app_user";

export const NEW_USER_TEMPLATE: IAppUser = {
  email: "",
  firstName: "",
  lastName: "",
  applications: {
    analytics: [],
    breadcrumb: [],
    hq: [],
    inventory: [],
  },
  id: null,
};

const initialState: IUsers = {
  all: [],
  posOnly: [],
  fetchedBrandId: "",
  editableUser: null,
  createUserResponse: {},

  allUsers: {
    requestStates: {
      [AllUsersRequestKey.AllUsers]: RequestStatus.NOT_STARTED,
      [AllUsersRequestKey.CreateUser]: RequestStatus.NOT_STARTED,
    },
  },

  analytics: {
    requestStates: {
      [AnalyticsRequestKey.FetchRoles]: RequestStatus.NOT_STARTED,
      [AnalyticsRequestKey.CreateMerchantUser]: RequestStatus.NOT_STARTED,
    },
    roles: [],
  },

  breadcrumb: {
    requestStates: {
      [BreadcrumbRequestKey.FetchRoles]: RequestStatus.NOT_STARTED,
      [BreadcrumbRequestKey.FetchZones]: RequestStatus.NOT_STARTED,
      [BreadcrumbRequestKey.CreatePosUser]: RequestStatus.NOT_STARTED,
    },
    roles: [],
    zones: [],
  },

  inventory: {
    requestStates: {
      [InventoryRequestKey.CreateUser]: RequestStatus.NOT_STARTED,
      [InventoryRequestKey.UpdateUserRoles]: RequestStatus.NOT_STARTED,
    },
  },
};

function users(
  state: IUsers = initialState,
  action:
    | AllUsersActionTypes
    | AnalyticsUserActionTypes
    | BreadcrumbUserActionTypes
    | InventoryUserActionTypes
): IUsers {
  switch (action.type) {
    case TypeKeys.RECEIVE_ALL_USERS: {
      const { all, posOnly, brandId } = action;
      return { ...state, all, posOnly, fetchedBrandId: brandId };
    }

    case TypeKeys.SAVE_CREATE_USER_RESPONSE: {
      return set(lensPath(["createUserResponse"]), action.response, state);
    }

    case TypeKeys.UPDATE_USERS_REQUEST_STATUS: {
      const { requestKey, requestStatus } = action;
      return set(
        lensPath(["allUsers", "requestStates", requestKey]),
        requestStatus,
        state
      );
    }

    case AnalyticsTypeKeys.SET_ANALYTICS_REQUEST_STATUS_ACTION: {
      const { requestKey, requestStatus } = action;
      return set(
        lensPath(["analytics", "requestStates", requestKey]),
        requestStatus,
        state
      );
    }

    case BreadcrumbTypeKeys.SET_BREADCRUMB_REQUEST_STATUS_ACTION: {
      const { requestKey, requestStatus } = action;
      return set(
        lensPath(["breadcrumb", "requestStates", requestKey]),
        requestStatus,
        state
      );
    }

    case InventoryTypeKeys.SET_INVENTORY_REQUEST_STATUS_ACTION: {
      const { requestKey, requestStatus } = action;
      return set(
        lensPath(["inventory", "requestStates", requestKey]),
        requestStatus,
        state
      );
    }

    case AnalyticsTypeKeys.FETCH_ROLES_ACTION: {
      const { roles } = action;
      return set(lensPath(["analytics", "roles"]), roles, state);
    }

    case BreadcrumbTypeKeys.FETCH_ROLES_ACTION: {
      const { roles } = action;
      return set(lensPath(["breadcrumb", "roles"]), roles, state);
    }

    case BreadcrumbTypeKeys.FETCH_ZONES_ACTION: {
      const { zones } = action;
      return set(lensPath(["breadcrumb", "zones"]), zones, state);
    }

    case InventoryTypeKeys.RECEIVE_USER_INV_ROLES: {
      const { roles, userId } = action;
      let newEditableUser = state.editableUser;
      const inventoryUsers =
        state.editableUser && state.editableUser.applications.inventory;
      if (state.editableUser && inventoryUsers) {
        const inventoryUserIndex = findIndex(
          propEq("id", userId),
          inventoryUsers
        );
        const updatedInventoryUser = merge(inventoryUsers[inventoryUserIndex], {
          roles,
        });
        const inventoryApplications = update(
          inventoryUserIndex,
          updatedInventoryUser,
          inventoryUsers
        );
        newEditableUser = mergeDeepRight(state.editableUser, {
          applications: {
            inventory: inventoryApplications,
          },
        });
      } else {
        newEditableUser = mergeDeepRight(state.editableUser, {
          applications: {
            inventory: [
              {
                roles,
              },
            ],
          },
        });
      }
      return {
        ...state,
        editableUser: newEditableUser,
      };
    }

    case InventoryTypeKeys.CREATE_USER_ACTION: {
      const { user } = action;
      const oldUser: IAppUser | undefined = findUserByEmail(
        user.email,
        state.all
      );

      if (oldUser) {
        const mergedInventoryUser = merge(
          head(oldUser.applications.inventory || []),
          user
        );
        const newUser = mergeDeepRight(oldUser, {
          applications: { inventory: [mergedInventoryUser] },
        });

        return {
          ...state,
          all: replaceInCollection(oldUser, newUser, state.all),
          editableUser: newUser,
        };
      } else {
        const newUserBase = state.editableUser || NEW_USER_TEMPLATE;
        const mergedInventoryUser = merge(
          head(newUserBase.applications.inventory || []),
          user
        );
        const newUser = mergeDeepRight(newUserBase, {
          applications: { inventory: [mergedInventoryUser] },
        });

        return {
          ...state,
          all: [...state.all, newUser],
          editableUser: newUser,
        };
      }
    }

    case TypeKeys.UPDATE_EDITABLE_USER: {
      return {
        ...state,
        editableUser: {
          ...state.editableUser,
          ...action.editableUser,
        },
      };
    }

    case TypeKeys.UPDATE_LOCAL_USER_RECORD: {
      const oldUser: IAppUser | undefined = findUserByEmail(
        action.user.email,
        state.all
      );

      if (oldUser) {
        return {
          ...state,
          all: replaceInCollection(oldUser, action.user, state.all),
          editableUser: {
            ...state.editableUser,
            ...action.user,
          },
        };
      } else {
        const isPosOnly =
          has("breadcrumb", action.user.applications) &&
          keys(action.user.applications).length === 1 &&
          !action.user.email;
        if (isPosOnly) {
          return {
            ...state,
            posOnly: [
              ...state.posOnly,
              ...(action.user.applications.breadcrumb || []),
            ],
          };
        }
        return {
          ...state,
          all: [...state.all, action.user],
          editableUser: {
            ...state.editableUser,
            ...action.user,
          },
        };
      }
    }

    case AnalyticsTypeKeys.UPDATE_LOCAL_USER_RECORD_WITH_NEW_MERCHANT_USER: {
      const oldUser: IAppUser | undefined = findUserByEmail(
        action.merchantUser.email,
        state.all
      );

      if (isNil(oldUser)) {
        /* eslint-disable no-console */
        console.error(
          "Could not find user attempting to be updated! email: ",
          action.merchantUser.email
        );

        return state;
      }

      // Currently at the brand-level we only support a single Analytics
      // user id under `applications`.
      const oldAnalytics = oldUser.applications.analytics
        ? oldUser.applications.analytics[0]
        : {};

      const newUser = {
        ...oldUser,
        applications: {
          ...oldUser.applications,
          analytics: [
            {
              ...oldAnalytics,
              id: action.merchantUser.id,
              firstName: action.merchantUser.firstName,
              lastName: action.merchantUser.lastName,
              roles: [],
            },
          ],
        },
      };

      return {
        ...state,
        all: replaceInCollection(oldUser, newUser, state.all),
        editableUser: newUser,
      };
    }

    case BreadcrumbTypeKeys.UPDATE_LOCAL_USER_RECORD_WITH_NEW_POS_USER: {
      const email = action.posUser.username;

      if (isNil(email)) {
        /* eslint-disable no-console */
        console.error(
          "Didn't receive an email address in response! action.posUser: ",
          action.posUser
        );

        return state;
      }

      const oldUser: IAppUser | undefined = findUserByEmail(email, state.all);

      if (isNil(oldUser)) {
        /* eslint-disable no-console */
        console.error(
          "Could not find user attempting to be updated! email: ",
          email
        );

        return state;
      }

      const oldBreadcrumb = oldUser.applications.breadcrumb
        ? oldUser.applications.breadcrumb
        : [];

      const newUser = {
        ...oldUser,
        applications: {
          ...oldUser.applications,
          breadcrumb: [
            ...oldBreadcrumb,
            {
              id: action.posUser.id,
              storeKey: action.storeKey,
              roles: [],
            },
          ],
        },
      };

      return {
        ...state,
        all: replaceInCollection(oldUser, newUser, state.all),
        editableUser: newUser,
      };
    }

    case TypeKeys.CLEAR_EDITABLE_USER:
      return {
        ...state,
        editableUser: null,
      };

    default: {
      return state;
    }
  }
}

export default users;
